import { faFilter } from "@fortawesome/free-solid-svg-icons";
import { Box } from "@material-ui/core";
import { LoadsAction } from "actions/loads";
import AgGridTable from "components/AgGrid/AgGridContainer";
import { Column as ColumnLayout } from "components/Layout";
import { SmallText, TitleText } from "components/Text";
import { AgGridTables } from "constants/aggrid";
import { Column } from "constants/columns";
import { useBoolean } from "hooks";
import { BaseColumnDefinitions } from "lib/agGrid/columnDefinitions";
import {
  dateXDaysAgo,
  formatDateTimeString,
  formatISO8601DateString,
} from "lib/timeFormats";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectLoadsForAgGrid } from "selectors";
import styled from "styled-components/macro";
import { SmallFaIcon } from "components/FaIcon";
import {
  faLightbulb,
  faRotateReverse,
} from "@fortawesome/pro-regular-svg-icons";
import { getDateRangeLabel } from "components/DateRangePicker/DateRangeRenderer";
import { ColDef } from "ag-grid-community";
import { rollUp } from "lib/agGrid/aggregators";
import { addDays } from "date-fns";
import { FilteringModal } from "./FilterForm";

const colDefs: ColDef<Loads>[] = [
  BaseColumnDefinitions[Column.Loads.AVERAGE_GRAMS],
  {
    headerName: "Buyer",
    children: [BaseColumnDefinitions[Column.Loads.BUYER_NAME]],
  },
  {
    headerName: "Buyer Way",
    children: [BaseColumnDefinitions[Column.Loads.BUYER_WAY_NAME]],
  },
  BaseColumnDefinitions[Column.Loads.CREATED],
  BaseColumnDefinitions[Column.Loads.LAST_MODIFIED],
  BaseColumnDefinitions[Column.Loads.DELIVERY_PEN],
  BaseColumnDefinitions[Column.Loads.DESTINATION_PIC],
  BaseColumnDefinitions[Column.Loads.LOAD_STATUS],
  {
    headerName: "Drafting Attributes",
    children: [
      BaseColumnDefinitions[Column.Loads.AGE],
      BaseColumnDefinitions[Column.Loads.BREED],
      BaseColumnDefinitions[Column.Loads.MARKS],
      BaseColumnDefinitions[Column.Loads.SALE_ROUND],
      BaseColumnDefinitions[Column.Loads.SEX],
      BaseColumnDefinitions[Column.Loads.SPECIES],
    ],
  },
  {
    headerName: "Livestock Sale",
    children: [
      BaseColumnDefinitions[Column.Loads.LIVESTOCK_SALE_CODE],
      BaseColumnDefinitions[Column.Loads.LIVESTOCK_SALE_DATE],
      BaseColumnDefinitions[Column.Loads.LIVESTOCK_SALE_ID],
      BaseColumnDefinitions[Column.Loads.LIVESTOCK_SALE_NAME],
    ],
  },
  BaseColumnDefinitions[Column.Loads.PROGENY],
  BaseColumnDefinitions[Column.Loads.QUANTITY],
  BaseColumnDefinitions[Column.Loads.QUANTITY_DELIVERED],
  BaseColumnDefinitions[Column.Loads.QUANTITY_WEIGHED],
  BaseColumnDefinitions[Column.Loads.SALE_LOT_STATUS],
  BaseColumnDefinitions[Column.Loads.SALEYARD_NAME],
  BaseColumnDefinitions[Column.Loads.THIRD_PARTY],
  BaseColumnDefinitions[Column.Loads.TOTAL_MASS_GRAMS],
  BaseColumnDefinitions[Column.Loads.TRANSPORTER_NAME],
];

export const DateRangeTextWrapper = styled.p`
  ${({ theme }) => `
  background-color: ${theme.colors.collapseBackground};
  padding: ${theme.space[1]}px;
  margin-right: auto;
  margin-left: auto;
  margin-top: ${theme.space[0]}px;
  margin-bottom: ${theme.space[0]}px;
  border-radius: ${theme.space[1]}px;
  align-self: center;
  ${({ fullWidth }) => fullWidth && "width: 100%"}
   `}
`;

export function LoadTable(): React.JSX.Element {
  const [isFiltersShowing, showFilters, hideFilters] = useBoolean(false);
  const [filters, setFilters] = useState({
    pickupDateGte: formatISO8601DateString(dateXDaysAgo(6)),
    pickupDateLte: formatISO8601DateString(addDays(new Date(), 1)),
  });
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const dispatch = useDispatch();

  // Helper to request loads and update lastRefresh
  const requestLoads = React.useCallback(() => {
    dispatch(LoadsAction.request(filters));
    setLastRefresh(new Date());
  }, [dispatch, filters]);

  // Fetch on mount and when filters change
  useEffect(() => {
    requestLoads();
  }, [requestLoads]);

  // Fetch every minute
  useEffect(() => {
    const interval = setInterval(() => {
      requestLoads();
    }, 60000);
    return () => clearInterval(interval);
  }, [requestLoads]);

  const loads = useSelector(selectLoadsForAgGrid);
  const extraActionComponents = [
    <DateRangeTextWrapper>
      <SmallFaIcon icon={faLightbulb} color="gray9C" />{" "}
      {getDateRangeLabel(
        new Date(filters.pickupDateGte),
        new Date(filters.pickupDateLte),
      )}
    </DateRangeTextWrapper>,
  ];

  const additionalActions = [
    {
      title: (
        <>
          Refresh
          {lastRefresh ? (
            <SmallText>Last: {formatDateTimeString(lastRefresh)}</SmallText>
          ) : null}
        </>
      ),
      icon: faRotateReverse,
      isDisabled: false,
      onClick: requestLoads,
    },
    {
      title: "Change Filters",
      icon: faFilter,
      isDisabled: false,
      onClick: showFilters,
    },
  ];

  return (
    <ColumnLayout fullHeight>
      <Box p={2}>
        <TitleText>Loads</TitleText>
      </Box>
      <AgGridTable
        columnDefs={colDefs}
        rowData={loads}
        showGlobalSearchFilter={false}
        tableName={AgGridTables.LOADS}
        getRowId={params => params.data.id}
        additionalActions={additionalActions}
        extraActionComponents={extraActionComponents}
        aggFuncs={{ rollUp }}
      />
      {isFiltersShowing && (
        <FilteringModal
          closeSelf={hideFilters}
          setFilters={setFilters}
          filters={filters}
        />
      )}
    </ColumnLayout>
  );
}
